/**
 * A hook for positioning an element based off an anchors position.
 */
import * as React from "react"
import getBoundingOffsets, { Offset } from "./getBoundingOffsets"
import getEdges from "./getEdges"
import { Preference } from "./getPreference"
import getPositionOffsets from "./getPositionOffsets"
import getPosition, { Edge } from "./getPosition"
import getScrollParents from "./getScrollParents"
import { getClient } from "../useMeasure"
import { toClientRect } from "./rect-utils"
import debounce from "./debounce"
import { equals, pick } from "ramda"

type Ref = React.RefObject<HTMLElement>
export type Preferences =
  | [Preference]
  | [Preference, Edge]
  | [Preference, Edge, Edge]
  | [Preference, Edge, Edge, Edge]
type DefaultProps = typeof defaultProps
type Props = Readonly<
  {
    anchorRef: Ref
    elementRef: Ref
    preferences: Preferences
  } & Partial<DefaultProps>
>

const defaultProps = {
  offset: { top: 0, left: 0 } as Offset
}

const useTether = (initialProps: Props) => {
  const {
    anchorRef,
    elementRef,
    preferences: [preference, ...availableEdges],
    offset
  } = {
    ...defaultProps,
    ...initialProps
  }

  const update = React.useCallback((): [Preference, ClientRect | undefined] => {
    // only update if element and anchor exist
    if (!anchorRef.current || !elementRef.current) {
      return [preference, undefined]
    }
    const offsets = getBoundingOffsets(
      anchorRef.current,
      elementRef.current,
      offset
    )

    // Calculate anchor rect based off of offsets because in some contexts, this
    // is different than what's returned from anchor.getClientBoundingRect().
    const anchorRect = toClientRect({
      top: offsets.top.bottom,
      left: offsets.left.right,
      height: offsets.bottom.top - offsets.top.bottom,
      width: offsets.right.left - offsets.left.right
    })
    const elementRect = {
      ...getClient(elementRef.current),
      top: 0,
      left: 0
    }
    const positionOffsets = getPositionOffsets(elementRect, offsets)

    const [primary] = preference.split("-") as [Edge, Edge | undefined]
    const preferredOffsets = pick([primary, ...availableEdges], positionOffsets)

    const edges = getEdges(preference, elementRect, preferredOffsets)
    const [primaryEdge] = edges

    const placement = getPosition(
      edges,
      anchorRect,
      elementRect,
      positionOffsets[primaryEdge]
    )
    return [primaryEdge, placement]
  }, [anchorRef, availableEdges, elementRef, offset, preference])

  const [state, setRect] = React.useState<[Preference, ClientRect | undefined]>(
    [preference, undefined]
  )

  // momoize callback
  const handleUpdate_ = React.useCallback(() => {
    const newState = update()
    // only set state if values are different
    if (!equals(state, newState)) {
      setRect(newState)
    }
  }, [state, update])

  // debounce memoized callback
  const handleUpdate = debounce(
    () =>
      new Promise(resolve => {
        handleUpdate_()
        resolve()
      })
  )

  // update if position changes
  React.useLayoutEffect(() => {
    handleUpdate()
  }, [handleUpdate])

  // update when refs change
  React.useLayoutEffect(() => {
    const [, position] = state
    if (position === undefined) {
      handleUpdate()
    }
  }, [anchorRef, elementRef, handleUpdate, state])

  React.useLayoutEffect(() => {
    const scrollParents = anchorRef.current
      ? getScrollParents(anchorRef.current)
      : []

    scrollParents.forEach(parent => {
      parent.addEventListener("scroll", handleUpdate, { passive: true })
    })

    window.addEventListener("resize", handleUpdate)

    return () => {
      scrollParents.forEach(parent => {
        parent.removeEventListener("scroll", handleUpdate)
      })

      window.removeEventListener("resize", handleUpdate)
    }
  }, [anchorRef, handleUpdate])

  return state
}

export default useTether
